import React from "react";
import { useTranslations } from "next-intl";
import Head from "next/head";
import { WEBPAGE_TITLE } from "@utils/Constants";
import Page from "@components/Layout/page";
import { Leaf } from "@components/Leaf/Leaf";
import { useInView } from "@hooks/useInView";
import styles from "./CompanyBirgjamatPage.module.scss";

export default function CompanyBirgjamatPage() {
    const t = useTranslations("CompanyBirgjamatPage");

    const [heroRef, heroInView] = useInView();
    const [overviewRef, overviewInView] = useInView();
    const [workflowRef, workflowInView] = useInView();
    const [ctaRef, ctaInView] = useInView();

    return (
        <Page fluid bodyClassName={styles.pageBody}>
            <Head>
                <title key="site_title">{"Birgjamat - " + WEBPAGE_TITLE}</title>
                <meta
                    name="keywords"
                    content="Birgjamat, birgjar, ESG, virðiskeðja"
                />
                <meta
                    property="og:title"
                    content={`${WEBPAGE_TITLE} - Birgjamat`}
                    key="og:title"
                />
                <meta
                    name="og:description"
                    content={t("hero_desc")}
                    key="og:description"
                />
                <meta name="description" content={t("hero_desc")} />
            </Head>

            <div className={styles.wrapper}>
                <Leaf className={styles.leafBg1} plain />
                <Leaf className={styles.leafBg2} plain />
                <Leaf className={styles.leafBg3} plain />

                <section
                    ref={heroRef}
                    className={`${styles.hero} ${styles.revealTarget} ${heroInView ? styles.revealed : ""}`}
                >
                    <div className={styles.heroContent}>
                        <span className={styles.badge}>{t("badge")}</span>
                        <h1 className={styles.heroTitle}>{t("hero_title")}</h1>
                        <p className={styles.heroSubtitle}>
                            {t("hero_subtitle")}
                        </p>
                        <p className={styles.heroDesc}>{t("hero_desc")}</p>
                        <a className={styles.ctaBtn} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>

                <div className={styles.showcase}>
                    <section
                        ref={overviewRef}
                        className={`${styles.showcaseRow} ${styles.revealTarget} ${overviewInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_overview_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_overview_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_overview_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.statGrid}>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                            <polyline points="14 2 14 8 20 8" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_assessment")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_assessment_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                                            <circle cx="9" cy="7" r="4" />
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_batch")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_batch_desc")}
                                    </span>
                                </div>
                                <div className={styles.statCard}>
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <line
                                                x1="18"
                                                y1="20"
                                                x2="18"
                                                y2="10"
                                            />
                                            <line
                                                x1="12"
                                                y1="20"
                                                x2="12"
                                                y2="4"
                                            />
                                            <line
                                                x1="6"
                                                y1="20"
                                                x2="6"
                                                y2="14"
                                            />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_compare")}
                                    </span>
                                    <span className={styles.statMeta}>
                                        {t("stat_compare_desc")}
                                    </span>
                                </div>
                                <div
                                    className={`${styles.statCard} ${styles.statCardAccent}`}
                                >
                                    <div className={styles.statIcon}>
                                        <svg
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        >
                                            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                                            <polyline points="22 4 12 14.01 9 11.01" />
                                        </svg>
                                    </div>
                                    <span className={styles.statLabel}>
                                        {t("stat_trust")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section
                        ref={workflowRef}
                        className={`${styles.showcaseRow} ${styles.reversed} ${styles.revealTarget} ${workflowInView ? styles.revealed : ""}`}
                    >
                        <div className={styles.showcaseText}>
                            <span className={styles.showcaseLabel}>
                                {t("showcase_workflow_label")}
                            </span>
                            <h2 className={styles.showcaseTitle}>
                                {t("showcase_workflow_title")}
                            </h2>
                            <p className={styles.showcaseDesc}>
                                {t("showcase_workflow_desc")}
                            </p>
                        </div>
                        <div className={styles.showcaseVisual}>
                            <div className={styles.workflowStack}>
                                <div className={styles.workflowCard}>
                                    <span className={styles.workflowNumber}>
                                        1
                                    </span>
                                    <span className={styles.workflowLabel}>
                                        {t("workflow_step_1")}
                                    </span>
                                </div>
                                <div className={styles.workflowCard}>
                                    <span className={styles.workflowNumber}>
                                        2
                                    </span>
                                    <span className={styles.workflowLabel}>
                                        {t("workflow_step_2")}
                                    </span>
                                </div>
                                <div className={styles.workflowCard}>
                                    <span className={styles.workflowNumber}>
                                        3
                                    </span>
                                    <span className={styles.workflowLabel}>
                                        {t("workflow_step_3")}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                <section
                    ref={ctaRef}
                    className={`${styles.bottomCta} ${styles.revealTarget} ${ctaInView ? styles.revealed : ""}`}
                >
                    <div className={styles.bottomCtaContent}>
                        <h2 className={styles.bottomCtaTitle}>
                            {t("bottom_title")}
                        </h2>
                        <p className={styles.bottomCtaDesc}>
                            {t("bottom_desc")}
                        </p>
                        <a className={styles.ctaBtnLight} href="/hafa-samband">
                            {t("contact_us")}
                        </a>
                    </div>
                </section>
            </div>
        </Page>
    );
}
